import useManyRemoteM1 from '@aims/shared/shared/use-many-remote-m1';
import {
  ScheduleFrequencies,
  ScheduleEntryStatuses,
} from '@aims/shared/report-schedules/constants';
import {
  allScheduledReportsForAdminQuery,
  extractAllScheduledReportsForAdmin,
} from '@aims/shared/report-schedules/scheduled-report-constants';
import { EyeOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Button,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import Column from 'antd/lib/table/Column';
import dayjs from 'dayjs';
import React, { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import CSPage from '../../components/CSPage';
import CSPageHeader from '../../components/CSPageHeader';
import settings from '../../settings';

const { Text } = Typography;

function ListScheduledReportsPage() {
  const [sortedBy, setSortedBy] = useState('updatedAt');
  const [sortOrder, setSortOrder] = useState('DESC');
  const filters = useRef({});
  const sortBy = useRef([
    { key: sortedBy, order: sortOrder },
    { key: '_score', order: 'DESC' },
  ]);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: settings.pageSize,
  });

  const {
    error,
    loading,
    data: reports,
    search: reportsSearch,
    hasNextPage,
    next,
    refetch,
  } = useManyRemoteM1({
    query: allScheduledReportsForAdminQuery,
    extract: extractAllScheduledReportsForAdmin,
    first: settings.querySize,
    filters: filters.current,
    sortBy: sortBy.current,
    fetchPolicy: 'network-only',
    queryId: 'listScheduledReportsPage',
  });

  const onFiltersChanged = useCallback(
    (_changed) => {
      const { search, ...changed } = _changed;
      if (search != undefined) {
        sortBy.current = [
          { key: '_score', order: 'DESC' },
          { key: sortedBy, order: sortOrder },
        ];
        reportsSearch(search);
      } else {
        filters.current = {
          ...filters.current,
          ...Object.entries(changed).reduce((prev, [k, v]) => {
            prev[k] = v ? v : undefined;
            return prev;
          }, {}),
        };
        refetch();
      }
    },
    [reportsSearch, refetch, sortedBy, sortOrder],
  );

  const handleTableChange = useCallback((params) => {
    setPagination({
      ...params.pagination,
    });
  }, []);

  return (
    <>
      <CSPage containerStyle={{ maxWidth: 'unset' }} title="Scheduled Reports">
        <CSPageHeader title="Scheduled Reports" />
        {error && (
          <div style={{ marginBottom: 16 }}>
            <Text type="danger">{error}</Text>
          </div>
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 16,
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <Form layout="inline" form={form} onValuesChange={onFiltersChanged}>
            <Form.Item name="search">
              <Input
                placeholder="Search reports"
                suffix={<SearchOutlined />}
                style={{ minWidth: 250 }}
              />
            </Form.Item>
            <Form.Item name="frequency">
              <Select
                placeholder="Frequency"
                allowClear
                style={{ minWidth: 130 }}
              >
                {Object.values(ScheduleFrequencies).map((c) => (
                  <Select.Option key={c.key} value={c.key}>
                    {c.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="status">
              <Select placeholder="Status" allowClear style={{ minWidth: 130 }}>
                {Object.values(ScheduleEntryStatuses).map((s) => (
                  <Select.Option key={s.key} value={s.key}>
                    {s.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
          <Button icon={<ReloadOutlined />} onClick={refetch}>
            Refresh
          </Button>
        </div>
        <Table
          dataSource={reports}
          loading={!reports.length && loading}
          pagination={{ ...pagination, total: reports.length }}
          onChange={handleTableChange}
          style={{ width: '100%' }}
          rowKey="_id"
        >
          <Column
            title="ID"
            dataIndex="humanReadableId"
            key="humanReadableId"
            width={100}
          />
          <Column
            title="Project #"
            dataIndex="holisticProject"
            key="projectNumber"
            width={130}
            render={(holisticProject) =>
              holisticProject?.projectNumber ||
              holisticProject?.projectNo ||
              '-'
            }
          />
          <Column
            title="Contact"
            dataIndex="contact"
            key="contact"
            render={(contact) => contact?.fullName || '-'}
          />
          <Column
            title="Frequency"
            dataIndex="frequency"
            key="frequency"
            width={110}
            render={(frequency) => (
              <Tag color={ScheduleFrequencies[frequency]?.color || 'default'}>
                {ScheduleFrequencies[frequency]?.label || frequency}
              </Tag>
            )}
          />
          <Column
            title="Due Date"
            dataIndex="dueDate"
            key="dueDate"
            width={130}
            render={(date) => (date ? dayjs(date).format('MMM D, YYYY') : '-')}
          />
          <Column
            title="Status"
            dataIndex="status"
            key="status"
            width={110}
            render={(status) => {
              const s = status?.toUpperCase();
              return (
                <Tag
                  color={
                    s === 'APPROVED'
                      ? 'green'
                      : s === 'SUBMITTED' || s === 'PENDING'
                      ? 'gold'
                      : s === 'REJECTED'
                      ? 'red'
                      : 'default'
                  }
                >
                  {status || 'Draft'}
                </Tag>
              );
            }}
          />
          <Column
            title="Submitted"
            dataIndex="submittedAt"
            key="submittedAt"
            width={130}
            render={(date) => (date ? dayjs(date).format('MMM D, YYYY') : '-')}
          />
          <Column
            title="Actions"
            key="actions"
            width={80}
            render={(_, record) => (
              <Space>
                <Tooltip title="Review">
                  <Link to={`/scheduled-reports/review/${record._id}`}>
                    <Button type="link" icon={<EyeOutlined />} />
                  </Link>
                </Tooltip>
              </Space>
            )}
          />
        </Table>
        {hasNextPage && (
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Button onClick={next} loading={loading}>
              Load More
            </Button>
          </div>
        )}
      </CSPage>
      <style jsx>{``}</style>
    </>
  );
}

export default ListScheduledReportsPage;
